import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, children, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3 pb-4">
      <div className="accent-rule w-24" />
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">{title}</h1>
          {subtitle ? <p className="mt-1 text-sm text-slate-600">{subtitle}</p> : null}
          {children}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

const KPI_TONES = {
  default: { value: "text-slate-900", bar: "from-indigo-500 to-violet-500", icon: "text-indigo-500" },
  primary: { value: "text-indigo-700", bar: "from-indigo-500 to-blue-500", icon: "text-indigo-500" },
  success: { value: "text-emerald-700", bar: "from-emerald-500 to-teal-500", icon: "text-emerald-500" },
  warning: { value: "text-amber-700", bar: "from-amber-500 to-orange-500", icon: "text-amber-500" },
  danger: { value: "text-rose-700", bar: "from-rose-500 to-pink-500", icon: "text-rose-500" },
};

export function KpiTile({ label, value, hint, tone = "default", testId, icon: Icon }) {
  const t = KPI_TONES[tone] || KPI_TONES.default;
  return (
    <div data-testid={testId} className="panel group relative overflow-hidden px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <span className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", t.bar)} />
      <div className="flex items-start justify-between gap-2 pt-1">
        <span className="kpi-label">{label}</span>
        {Icon ? <Icon className={cn("h-4 w-4 shrink-0 transition-transform duration-200 group-hover:scale-110", t.icon)} /> : null}
      </div>
      <div className={cn("kpi-value mt-1.5", t.value)}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

export function EmptyState({ title, description, action, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-start gap-2 px-4 py-10">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {description ? <p className="max-w-lg text-sm text-slate-500">{description}</p> : null}
      {action}
    </div>
  );
}

export function Section({ title, subtitle, actions, children, className, testId }) {
  return (
    <section data-testid={testId} className={cn("panel overflow-hidden", className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
          <div>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h2>
            {subtitle ? <p className="text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}
